import { memo, useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useStore } from 'store/StoreContext';
import { saveGenerator as saveGeneratorKiyaku } from 'store/connect/ConnectKiyakuData';
import { saveGenerator as saveGeneratorV1 } from 'store/connect/AuthorizeV1Data';
import { saveGenerator as saveGeneratorV2 } from 'store/connect/AuthorizeV2Data';
import { callAgreeApi } from 'requests/RequestUser';
import { RoutePath } from 'consts/RoutePath';
import classes from './override.module.scss';
import { CircularProgress } from '@material-ui/core';
import { HttpStatus } from 'consts/HttpStatus';
import { callCheckWhiteListApi } from 'requests/RequestWhiteList';
import { ReactError } from 'consts/ReactError';
import { useReceiveV2AuthorizeRequest } from 'hooks/UseReceiveV2AuthorizeRequest';

type Props = {};

export const TermsTemplate = memo((_props: Props) => {
	const [store, dispatch] = useStore(); // eslint-disable-line
	const history = useHistory();
	const location = useLocation();
	const search = location.search;
	const queryParams = new URLSearchParams(search);
	const fromType = queryParams.get('from'); // 遷移元種別
	const isFirstAgreement = queryParams.get('isFirstAgreement') ? 'true' === queryParams.get('isFirstAgreement') : true; // 初回規約同意か
	const { v2Params } = useReceiveV2AuthorizeRequest();
	const [formData, setFormData] = useState({ terms_agree: 0 });
	const [formError, setFormError] = useState('');
	const isFormError = formError !== '';
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [isCompleteButtonDisabled, setIsCompleteButtonDisabled] = useState(true);
	const [processLoading, setProcessLoading] = useState(false);

	const handleChangeCheckValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, [event.target.name]: event.target.checked ? 1 : 0 }));
		setIsCompleteButtonDisabled(!event.target.checked);
	};

	const complete = async () => {
		setProcessLoading(true);

		setFormError('');
		setActionError('');

		if (formData.terms_agree !== 1) {
			setFormError('規約に同意してください。');
			setProcessLoading(false);
			return;
		}

		// 規約同意更新
		const updateAgreeResult = await callAgreeApi();
		if (updateAgreeResult.status !== HttpStatus.OK) {
			setActionError(ReactError.E9999);
			history.push(RoutePath.ERROR);
			return;
		}

		history.push(`${RoutePath.TERMS_COMPLETE}?from=${fromType}`);
	};

	useEffect(() => {
		const execute = async () => {
			if (!!fromType) {
				// ストアに格納
				if ('othersite' === fromType) {
					setProcessLoading(true);
					const burl = queryParams.get('burl');
					const bsn = queryParams.get('bsn');
					const scd = queryParams.get('scd');
					const result = await callCheckWhiteListApi({
						burl,
						bsn,
						scd,
					});
					if (result.status === HttpStatus.REDIRECT) {
						history.push(RoutePath.ERROR);
						setProcessLoading(false);
						return;
					}
					saveGeneratorKiyaku(dispatch, { scd, bsn, burl });
					setProcessLoading(false);
				} else if ('commonloginv1' === fromType) {
					setProcessLoading(true);
					const burl = queryParams.get('burl');
					const bsn = queryParams.get('bsn');
					const scd = queryParams.get('scd');
					const shopcd = queryParams.get('shopcd');
					const result = await callCheckWhiteListApi({
						burl,
						bsn,
						scd,
					});
					if (result.status === HttpStatus.REDIRECT) {
						history.push(RoutePath.ERROR);
						setProcessLoading(false);
						return;
					}
					saveGeneratorV1(dispatch, { burl, bsn, scd, shopcd });
					setProcessLoading(false);
					return;
				} else if ('commonloginv2' === fromType) {
					setProcessLoading(true);
					const result = await callCheckWhiteListApi({
						burl: v2Params.redirect_uri,
						scd: v2Params.client_id,
					});
					if (result.status === HttpStatus.REDIRECT) {
						history.push(RoutePath.ERROR);
						setProcessLoading(false);
						return;
					}
					saveGeneratorV2(dispatch, v2Params);
					setProcessLoading(false);
					return;
				}
			}
		};
		execute();
	}, [fromType]);

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = 'GakkenID規約同意';
	}, []);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError) {
			errorRef.current.scrollIntoView();
		}
	}, [formError]);

	return (
		<main>
			<div className="section">
				<div className="section-in" ref={scrollRef}>
					{!!fromType ? (
						<>
							<div className="section-head">規約同意</div>

							<div className="section-txt-box">
								{isFirstAgreement ? (
									<div>
										<div className="txt-head">
											<span>「規約に同意する」にチェックをし、完了ボタンを押下してください。</span>
										</div>
										<span>
											※こどもIDの登録をご希望の場合は、規約に同意いただくことで、こどもIDユーザーも、規約に同意したものとみなします。
										</span>
									</div>
								) : (
									<div>
										<div className="txt-head">
											<span>新しいバージョンの規約があります。</span>
											<br />
											<span>「規約に同意する」にチェックをし、「完了ボタン」を押下してください。</span>
										</div>
										<span>
											※こどもIDのご登録がある場合は、規約に同意いただくことで、こどもIDユーザーも、規約に同意したものとみなします。
										</span>
									</div>
								)}

								<ul className="link-lists">
									<li>
										<a href="/agreement" target="_blank">
											GakkenID規約
										</a>
									</li>
									<li>
										<a href="https://gakken-ps.co.jp/privacypolicy/" target="_blank">
											個人情報について
										</a>
									</li>
								</ul>
							</div>

							{isFormError || isActionError ? (
								<div className="error-box" ref={errorRef}>
									{isActionError ? <p>{actionError}</p> : null}
									{isFormError ? (
										<>
											<p>※下記項目は全て必須となります。</p>
											<div className="error-box-in">{formError !== '' ? <p>【規約同意】{formError}</p> : null}</div>
										</>
									) : null}
								</div>
							) : null}

							<div className="form-box">
								<div className="form-input">
									<div className="check-box">
										<ul>
											<li>
												<label
													className={`check-label ${formData.terms_agree === 1 ? 'is-active' : ''}`}
													htmlFor="terms_agree"
												>
													<input
														type="checkbox"
														id="terms_agree"
														name="terms_agree"
														checked={formData.terms_agree === 1}
														onChange={handleChangeCheckValue}
													/>
													規約に同意する
												</label>
											</li>
										</ul>
									</div>
								</div>
							</div>

							<div className="btn-box">
								<div className="btn gr">
									{processLoading ? (
										<CircularProgress />
									) : (
										<button
											className={isCompleteButtonDisabled ? classes.disabled : ''}
											onClick={complete}
											disabled={isCompleteButtonDisabled}
										>
											完了
										</button>
									)}
								</div>
							</div>
						</>
					) : (
						<>
							<div className="error-box">
								<div className="error-box-in">
									<p>必要なパラメータが存在しません。</p>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</main>
	);
});
